import { Component, OnDestroy } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  cilLockLocked,
  cilBuilding,
  cilLayers,
  cilHome,
  cilPhone,
  cilBriefcase,
  cilLocationPin,
  cilEnvelopeClosed,
  cilFingerprint,
} from '@coreui/icons';
import { environment } from '../../../../environments/environment';
import {
  Subject,
  Subscription,
  debounceTime,
  scan,
  startWith,
  shareReplay,
} from 'rxjs';

type RegisterDraft = {
  adminAccessKey: string;
  email: string;
  password: string;
  repeatPassword: string;
  companyName: string;
  companyType: string;
  branchOffice: string;
  phoneNumber: string;
  address: string;
  agency: string;
  admin: boolean;
  language: string;
};

function detectBrowserLang(): string {
  const supported = ['en', 'tr', 'es', 'el', 'de'];
  const browser = typeof navigator !== 'undefined'
    ? navigator.language.split('-')[0].toLowerCase()
    : 'en';
  return supported.includes(browser) ? browser : 'en';
}

const INITIAL_DRAFT: RegisterDraft = {
  adminAccessKey: '',
  email: '',
  password: '',
  repeatPassword: '',
  companyName: '',
  companyType: '',
  branchOffice: '',
  phoneNumber: '',
  address: '',
  agency: '',
  admin: false,
  language: detectBrowserLang(),
};

@Component({
    selector: 'app-register',
    templateUrl: './register.component.html',
    styleUrls: ['./register.component.scss'],
    standalone: false
})
export class RegisterComponent implements OnDestroy {
  showSuccessScreen = false;

  // Icons
  icons = {
    cilEnvelopeClosed,
    cilLockLocked,
    cilFingerprint,
    cilBuilding,
    cilLayers,
    cilHome,
    cilPhone,
    cilBriefcase,
    cilLocationPin,
  };

  // Template-driven binding object (simple)
  formData: RegisterDraft = { ...INITIAL_DRAFT };

  // Buffered state via RxJS
  private updates$ = new Subject<Partial<RegisterDraft>>();
  private sub!: Subscription;
  private latestDraft: RegisterDraft = { ...INITIAL_DRAFT };

  // UI
  step: 1 | 2 = 1;
  loading = false;
  passwordMismatch = false;
  serverError: string | null = null;

  constructor(private http: HttpClient) {
    const draft$ = this.updates$.pipe(
      debounceTime(120),
      startWith<Partial<RegisterDraft>>({}),
      // seed with full type so accumulator is RegisterDraft
      scan<Partial<RegisterDraft>, RegisterDraft>(
        (acc, curr) => ({ ...acc, ...curr } as RegisterDraft),
        { ...INITIAL_DRAFT }
      ),
      shareReplay({ bufferSize: 1, refCount: true })
    );

    this.sub = draft$.subscribe((d) => (this.latestDraft = d));
  }

  // Collect field changes into the buffer
  update<K extends keyof RegisterDraft>(key: K, value: RegisterDraft[K]) {
    // ✅ immediate mirror so validators & buttons see the latest value right away
    (this.latestDraft as any)[key] = value;
    this.updates$.next({ [key]: value } as Partial<RegisterDraft>);
  }

  // ————— Step validation (simple, fast, client-side) —————
  get step1Valid(): boolean {
    const d = this.latestDraft;
    const emailOk = !!d.email && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email);
    const passLen = (d.password || '').length >= 10;
    const passMatch = d.password === d.repeatPassword && !!d.password;
    const keyOk = !!d.adminAccessKey;
    return emailOk && passLen && passMatch && keyOk;
  }

  get step2Valid(): boolean {
    const d = this.latestDraft;
    return (
      !!d.companyName &&
      !!d.companyType &&
      !!d.branchOffice &&
      !!d.phoneNumber &&
      !!d.address
    );
  }

  next() {
    // make sure the latest keystrokes are flushed
    this.updates$.next({});
    // inline password mismatch flag for the UI
    this.passwordMismatch =
      this.latestDraft.password !== this.latestDraft.repeatPassword;
    if (!this.step1Valid) return;
    this.step = 2;
  }

  back() {
    this.step = 1;
  }

  onSubmit() {
    this.serverError = null;
    // flush any pending changes
    this.updates$.next({});

    // hard stop if something is off
    if (!this.step1Valid || !this.step2Valid) return;

    const d = this.latestDraft;

    const apiUrl = `${environment.baseUrl}/post/register`;

    // Header: EXACT auth header (no Bearer)
    const headers = new HttpHeaders({
      Authorization: d.adminAccessKey || '',
    });

    // Body: exclude adminAccessKey & repeatPassword
    const payload = {
      email: d.email,
      companyName: d.companyName,
      password: d.password,
      companyType: d.companyType.toLowerCase().trim(),
      branchOffice: d.branchOffice,
      phoneNumber: d.phoneNumber,
      address: d.address,
      admin: d.admin,
      agency: "default",
      language: d.language,
    };

    this.loading = true;

    // ✅ EXACT original call signature
    this.http.post(apiUrl, payload, { headers }).subscribe({
      next: (response) => {
        console.log('Registration successful:', response);
        this.loading = false;
        this.showSuccessScreen = true;
      },
      error: (error) => {
        console.error('Error during registration:', error);
        this.loading = false;
        alert(
          error?.error?.message || 'An error occurred during registration.'
        );
      },
    });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }
  // Strength label based on latest buffered value
  get passwordStrengthLevel(): 'weak' | 'medium' | 'strong' {
    const v = String(this.latestDraft.password || '');
    let score = 0;
    if (v.length >= 10) score++;
    if (/[A-Z]/.test(v) && /[a-z]/.test(v)) score++;
    if (/\d/.test(v)) score++;
    if (/[^A-Za-z0-9]/.test(v)) score++;
    if (score >= 3) return 'strong';
    if (score === 2) return 'medium';
    return 'weak';
  }

  // Progress bar value (33 / 66 / 100)
  get passwordStrengthValue(): number {
    return this.passwordStrengthLevel === 'strong'
      ? 100
      : this.passwordStrengthLevel === 'medium'
      ? 66
      : 33;
  }

  showPassword = false; // toggle state

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  redirectToMain() {
    window.location.href = environment.baseUrl;
  }
}
